import { I18nService } from '@core/i18n-legacy';
import {
  ReportDashboardComponentChartType,
  ReportDashboardComponentKind,
  ReportDashboardComponentViewMode,
  ReportsTranslations,
  Blueprint,
  DatasetBase,
  ReportDashboardComponentViewData,
  ReportDashboardMetricOptions,
  ReportsBarChartLabelOptions,
  ReportsBarChartOptions,
} from '@core/reports-types';
import { datasetValueFormatterOptions } from '@core/reports-util';
import type { DurationPipe } from '@core/user-interface-legacy';
import { SupportedNumberFormat, valueFormatter, pipeCompose } from '@core/utils-legacy';

import { BAR_CHART_BAR_MAX_WIDTH } from '../chart-layout';
import { addPropertyFactory } from '../config-factories/helpers';
import { addXAxisDurationLabelFactory, createXAxisTypeValue } from '../config-factories/x-axis';
import { createYAxisCategoryFactory } from '../config-factories/y-axis';
import { dataZoom } from '../datazoom';
import { getXAxisNameLabel } from '../get-x-axis-name-label';
import { xAxisMediaQueries } from '../x-axis-media-queries';
import { funnelTooltipContextFactory } from './tooltip';

const createSeriesLabelSettings = (
  dataset: DatasetBase,
  i18n: I18nService,
): ReportsBarChartLabelOptions => ({
  show: true,
  silent: true,
  padding: 0,
  margin: 0,
  position: 'right',
  formatter: params => {
    if (!params.encode!.x) return '';
    const data = Array.isArray(params.data) ? params.data : [params.data];
    const value = <number | string>data[params.encode!.x[0]];
    return valueFormatter(value, datasetValueFormatterOptions(dataset), i18n, {
      number: { format: SupportedNumberFormat.IntlCompact, zeroReplacement: '' },
    });
  },
});

const addBarChartSeriesFactory =
  (dataset: DatasetBase, i18n: I18nService) =>
  <T>(opts: T) => ({
    ...opts,
    series: (dataset.dimensions.length > 2
      ? dataset.dimensions.slice(1, -1)
      : dataset.dimensions.slice(1)
    ).map(dimension => ({
      id: dimension.id || dimension.name,
      name: dimension.name,
      type: <const>'bar',
      seriesLayoutBy: <const>'column',
      label: createSeriesLabelSettings(dataset, i18n),
      emphasis: {
        label: createSeriesLabelSettings(dataset, i18n),
      },
      barMaxWidth: BAR_CHART_BAR_MAX_WIDTH,
    })),
  });

const addXASxisMediaQueriesFactory =
  (metric: ReportDashboardMetricOptions) =>
  <T>(opts: T) => ({
    ...opts,
    media: [...xAxisMediaQueries(metric)],
  });

const getDatasetFactory = (dataset: DatasetBase) => () => ({
  dataset: dataset.dataset,
});

const addDataZoomFactory =
  (dataset: DatasetBase, component: ReportDashboardComponentViewData) =>
  <T extends {}>(opts: T) => ({
    ...opts,
    dataZoom: dataZoom(component, dataset.getRawData()),
  });

const addTooltipFactory =
  (dataset: DatasetBase, translations: ReportsTranslations) =>
  <T extends {}>(opts: T) => ({
    ...opts,
    tooltip: {
      show: true,
      trigger: <const>'axis',
      axisPointer: {
        type:
          dataset.datasetSize.rows > 2 && dataset.datasetSize.cols > 2
            ? <const>'shadow'
            : <const>'none',
      },
      formatter: (params: any) => funnelTooltipContextFactory(dataset, translations, params),
    },
  });

const addXAxisNameLabelFactory =
  (metric: ReportDashboardMetricOptions) =>
  <T extends {}>(opts: T) => ({
    ...opts,
    graphic: [getXAxisNameLabel(metric.xAxis)],
  });

const funnelBarChart = (
  component: ReportDashboardComponentViewData,
  dataset: DatasetBase,
  metric: ReportDashboardMetricOptions,
  translations: ReportsTranslations,
) => {
  const xAxis = pipeCompose(
    createXAxisTypeValue,
    addXAxisDurationLabelFactory(dataset, translations.i18n),
  );

  const yAxis = createYAxisCategoryFactory(dataset)();

  return pipeCompose(
    getDatasetFactory(dataset),
    addBarChartSeriesFactory(dataset, translations.i18n),
    addXASxisMediaQueriesFactory(metric),
    addDataZoomFactory(dataset, component),
    addTooltipFactory(dataset, translations),
    addDataZoomFactory(dataset, component),
    addPropertyFactory('xAxis', xAxis),
    addPropertyFactory('yAxis', yAxis),
    addXAxisNameLabelFactory(metric),
  ) satisfies ReportsBarChartOptions<'value', 'category'>;
};

export const funnelBarChartBlueprint = (
  translations: ReportsTranslations,
  durationPipe: DurationPipe,
): Blueprint<any> => ({
  match: {
    kind: ReportDashboardComponentKind.Funnel,
    viewMode: ReportDashboardComponentViewMode.Chart,
    chartType: ReportDashboardComponentChartType.Bar,
  },
  factory: ({ component, dataset: originalDataset, metric, meta }) => {
    let dataset = originalDataset;
    if (meta.truncateDataset) {
      const truncateLength =
        typeof meta.truncateDataset === 'number'
          ? meta.truncateDataset
          : meta.truncateDataset(component, dataset);
      dataset = dataset.truncateDataset('first', truncateLength);
    }
    return {
      vis: funnelBarChart(component, dataset, metric, translations),
      dataset,
    };
  },
});
